/**
 * Redux slice for user profile and settings.
 *
 * @remarks
 * User settings are stored locally in the serviceman-api database
 * (not proxied to upstream). The fetch response is mapped from the
 * flat backend shape into separate `profile` and `settings` objects.
 * The update endpoint returns data directly (not wrapped in `result`).
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'
import api from '../../api/axios'
import { API_ENDPOINTS } from '../../api/endpoints'
import { getApiErrorMessage } from '../../utils/apiError'
import { User, UserSettings } from '../../types/user'

/** Redux state for the user feature slice. */
interface UserState {
  profile: User | null
  settings: UserSettings | null
  loading: boolean
  error: string | null
}

const initialState: UserState = {
  profile: null,
  settings: null,
  loading: false,
  error: null,
}

// Async Thunks

/**
 * Fetch user settings and profile from the backend.
 *
 * @remarks
 * Backend: `GET /serviceman/user/settings/query`.
 * Response is mapped into `UserState.profile` and `UserState.settings`
 * with default fallbacks for missing fields.
 *
 * @throws Rejects with Finnish error string on failure
 */
export const fetchUserSettings = createAsyncThunk('user/fetchSettings', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<{ result: any }>(API_ENDPOINTS.USER_SETTINGS_QUERY)
    return response.data.result
  } catch (error: any) {
    console.error('Failed to fetch user settings:', error)
    return rejectWithValue(getApiErrorMessage(error, 'errors.loadFailed'))
  }
})

/**
 * Update user settings and profile data.
 *
 * @remarks
 * Backend: `PUT /serviceman/user/settings/update`.
 * Response is returned directly (not wrapped in `result`).
 * Both `profile` and `settings` sub-states are patched from the response.
 *
 * @param data - Partial settings object with optional `userName`, `language`, `notifications`
 * @throws Rejects with Finnish error string on failure
 */
export const updateUserSettings = createAsyncThunk(
  'user/updateSettings',
  async (data: { userName?: string; language?: string; notifications?: any }, { rejectWithValue }) => {
    try {
      const response = await api.put(API_ENDPOINTS.USER_SETTINGS_UPDATE, data)
      // Backend returns data directly, not wrapped in result
      return response.data
    } catch (error: any) {
      console.error('Failed to update user settings:', error)
      return rejectWithValue(getApiErrorMessage(error, 'settings.saveFailed'))
    }
  }
)

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    setUserLanguage: (state, action: PayloadAction<'fi' | 'sv' | 'en'>) => {
      if (state.profile) {
        state.profile.language = action.payload
      }
    },
  },
  extraReducers: builder => {
    // Fetch settings
    builder
      .addCase(fetchUserSettings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload

        // Get language from localStorage (i18n), not from backend
        // Language is a UI preference that doesn't need backend persistence
        // Normalize to base code only (e.g. 'fi-FI' → 'fi') — browser-detected
        // locales can be full BCP-47 tags that don't match Select option values.
        const validLangs: Record<string, 'fi' | 'sv' | 'en'> = { fi: 'fi', sv: 'sv', en: 'en' }
        const rawLang = localStorage.getItem('i18nextLng') || ''
        const storedLanguage = validLangs[rawLang.split('-')[0]] ?? 'fi'

        // Map backend response to frontend structure
        // Fall back to localStorage email (set during PIN auth) when the
        // ServiceManSettings record doesn't exist yet in the backend DB.
        state.profile = {
          id: data.id || 0,
          email: data.email || localStorage.getItem('email') || '',
          name: data.userName || '',
          userName: data.userName || '',
          phone: data.phone || '',
          role: data.role || 'serviceman',
          organizationId: data.organizationId || 0,
          language: storedLanguage,
          companyAccess: data.companyAccess || [],
          isActive: data.isActive !== undefined ? data.isActive : true,
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString(),
        }
        state.settings = {
          language: storedLanguage,
          notifications: data.notifications || {
            email: true,
            push: false,
            maintenanceUpdates: true,
          },
        }
      })
      .addCase(fetchUserSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update settings
    builder
      .addCase(updateUserSettings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload
        // Update both profile and settings with response
        if (state.profile) {
          state.profile.name = data.userName || state.profile.name
          state.profile.userName = data.userName || state.profile.userName
          state.profile.phone = data.phone || state.profile.phone
          state.profile.language = data.language || state.profile.language
        }
        if (state.settings) {
          state.settings.language = data.language || state.settings.language
          state.settings.notifications = data.notifications || state.settings.notifications
        }
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

// Actions
export const { clearError, setUserLanguage } = userSlice.actions

// Selectors
/** Select the full user sub-state. */
export const selectUser = (state: RootState) => state.user
/** Select the user profile (or `null` if not yet loaded). */
export const selectUserProfile = (state: RootState) => state.user.profile
/** Select the user settings (or `null` if not yet loaded). */
export const selectUserSettings = (state: RootState) => state.user.settings
/** Select the loading flag for user operations. */
export const selectUserLoading = (state: RootState) => state.user.loading
/** Select the current user error message (or `null`). */
export const selectUserError = (state: RootState) => state.user.error

// Reducer
export default userSlice.reducer
